import { useOrganization } from "@clerk/nextjs";
import { useRouter } from "next/router";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo } from "react";

import { AppLoader } from "~/components/_layout/AppLoader";
import { Nullable } from "~/shared/types/Nullable";
import { api } from "~/utils/api";

type OrgSetting = {
  id: string;
  key: string;
  value?: string;
};

type OrgContextState = {
  orgBlockTransactionsBefore: Nullable<Date>;
  orgClerkId: string;
  orgId: string;
  orgIcon: Nullable<string>;
  orgImage: Nullable<string>;
  orgIsEnabled: boolean;
  orgIsOnboarding: boolean;
  orgName: string;
  orgSettings: OrgSetting[];
  orgTimezone: Nullable<string>;
  /** Org GL/base currency (ISO 4217). Drives money display symbol; defaults to USD. */
  orgBaseCurrencyCode: string;
  /** Fallback transaction currency for new documents (ISO 4217); defaults to USD. */
  orgDefaultTransactionCurrencyCode: string;
  getOrgSetting: (key: string) => Nullable<string>;
};

const OrgContext = createContext<OrgContextState | undefined>(undefined);

const normalizeStringValue = (input?: string | null) => input ?? "";
const routeSlugToClerkSlug = (routeOrgSlug: string | string[] | undefined): string | null => {
  const value = Array.isArray(routeOrgSlug) ? routeOrgSlug[0] : routeOrgSlug;
  if (!value?.startsWith("org-")) {
    return null;
  }
  return value.slice(4);
};

export const OrgProvider = (props: PropsWithChildren): JSX.Element => {
  const router = useRouter();
  const { isLoaded: isClerkOrgLoaded, organization } = useOrganization();
  const currentOrg = api.org.current.useQuery(undefined, { refetchOnMount: "always" });
  const currentOrgSettings = api.org.retrieveSettings.useQuery(undefined, { enabled: !!currentOrg.data?.id });
  const routeOrgSlug = routeSlugToClerkSlug(router.query.orgSlug);
  const activeOrgSlug = organization?.slug ?? null;
  const currentOrgClerkMismatch =
    !!routeOrgSlug && !!organization?.id && !!currentOrg.data?.clerkId && currentOrg.data.clerkId !== organization.id;
  const orgRouteMismatch = !!routeOrgSlug && isClerkOrgLoaded && routeOrgSlug !== activeOrgSlug;
  const shouldRedirectToRouteOrg = orgRouteMismatch || currentOrgClerkMismatch;

  useEffect(() => {
    if (!router.isReady || !shouldRedirectToRouteOrg || !routeOrgSlug) {
      return;
    }

    const redirectToSwitchOrg = async () => {
      await router.replace({
        pathname: "/switch-org",
        query: {
          redirect_org: routeOrgSlug,
          redirect_url: router.asPath,
        },
      });
    };

    void redirectToSwitchOrg();
  }, [routeOrgSlug, router, shouldRedirectToRouteOrg]);

  const getOrgSetting = useCallback(
    (key: string): string | null => {
      if (!currentOrgSettings.data) {
        return null;
      }
      return currentOrgSettings.data.find(x => x.key === key)?.value ?? null;
    },
    [currentOrgSettings.data],
  );

  const context = useMemo<OrgContextState>(
    () => ({
      orgBlockTransactionsBefore: currentOrg.data?.blockTxnsBefore ?? null,
      orgClerkId: normalizeStringValue(currentOrg.data?.clerkId),
      orgId: normalizeStringValue(currentOrg.data?.id),
      orgIcon: normalizeStringValue(currentOrg.data?.iconUrl),
      orgImage: normalizeStringValue(currentOrg.data?.imageUrl),
      orgIsEnabled: currentOrg.data?.isEnabled ?? false,
      orgIsOnboarding: currentOrg.data?.isOnboarding ?? false,
      orgName: normalizeStringValue(currentOrg.data?.name),
      orgSettings: currentOrgSettings.data ?? [],
      orgTimezone: currentOrg.data?.timezone ?? null,
      orgBaseCurrencyCode: currentOrg.data?.baseCurrencyCode ?? "USD",
      orgDefaultTransactionCurrencyCode: currentOrg.data?.defaultTransactionCurrencyCode ?? "USD",
      getOrgSetting,
    }),
    [currentOrg.data, currentOrgSettings.data, getOrgSetting],
  );

  const isVerifyingRouteOrg = !!routeOrgSlug && currentOrg.isFetching && !currentOrg.data;
  const isLoading =
    currentOrg.isPending || isVerifyingRouteOrg || (currentOrgSettings.isPending && currentOrgSettings.fetchStatus !== "idle");
  if (shouldRedirectToRouteOrg) {
    return <AppLoader label="Switching organization" isLoading />;
  }
  if (isLoading) {
    return <AppLoader label="Loading your Org details" isLoading={isLoading} />;
  }

  return <OrgContext.Provider value={context}>{props.children}</OrgContext.Provider>;
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrgContextProvider");
  }
  return context;
};

/**
 * Safe version of useOrg that returns null when used outside OrgProvider.
 * Use this in shared components that may render outside org context.
 */
export const useOptionalOrg = (): OrgContextState | null => {
  return useContext(OrgContext) ?? null;
};

/**
 * Returns the org's timezone with a fallback to UTC.
 *
 * ShelfCycle Standard: Org timezone should ALWAYS drive display and date calculations,
 * NEVER the local browser timezone nor raw UTC. This hook provides consistent access
 * to the org timezone for date boundary calculations (start/end of day), display formatting,
 * and API queries that filter by date ranges.
 *
 * This hook must be used within an OrgProvider. Using it outside the provider will throw
 * to prevent silently using the wrong timezone (which can cause serious data correctness issues).
 *
 * See docs/TIMEZONE_STANDARDS.md for comprehensive timezone handling documentation.
 *
 * @throws {Error} If used outside OrgProvider
 * @example
 * const timezone = useOrgTimezone();
 * const endOfDay = DateTime.fromJSDate(date).setZone(timezone).endOf("day");
 */
/** The org's timezone where an org is in context, else UTC, for components that also render outside the org shell. */
export const useOrgTimezoneOrUtc = (): string => useContext(OrgContext)?.orgTimezone ?? "UTC";

export const useOrgTimezone = (): string => {
  const { orgTimezone } = useOrg();
  return orgTimezone ?? "UTC";
};
